// Push registration + native deep-link listeners (GATE-2 client / GATE-3 push). Native-only glue:
// every entry no-ops on web (there is no APNs token in a browser), mirroring token-provider's isNative branch.
// Kept app-agnostic (no @cntrl/api-client / config import): the caller injects the token-POST and the deep-link
// controller, so this package never learns the app's routes or HTTP client — matching how token-provider stays
// config-free.
import { Capacitor } from "@capacitor/core";
import type { PluginListenerHandle } from "@capacitor/core";
import { PushNotifications } from "@capacitor/push-notifications";
import { App } from "@capacitor/app";
import { getPlatform } from "./platform.js";
import { type DeepLinkController, parseToParam } from "./deep-link.js";
import { type ApnsEnvironment, resolveApnsEnvironment } from "./push-environment.js";

/** The `/me/push/register` body the app POSTs. A device registers for the signed-in PERSON, never a band
 * (ART-259); `appEnv` is the APNs environment the build was signed for. */
export interface PushRegistrationRequest {
  token: string;
  platform: string;
  appEnv: string;
  deviceModel?: string;
  appVersion?: string;
}

export interface RegisterPushOptions {
  /** The FALLBACK APNs environment (`'production' | 'sandbox'`, derived from the build lane). The registered value
   * is what the signed build itself says ({@link resolveApnsEnvironment}); this is used only where that cannot be
   * read (web, an older wrapper). The lane is the wrong axis for it: a TestFlight build on qa is production APNs. */
  appEnv: ApnsEnvironment;
  /** The build number stamped into `__ENV__` — reported as `appVersion`. */
  appVersion?: string;
  /** POST the token to `/me/push/register` (the app wires this over its ApiClient — keeps this package
   * HTTP-client-agnostic). Called from the `'registration'` event. */
  postToken: (request: PushRegistrationRequest) => Promise<void>;
  /** Optional registration-error sink (APNs `registrationError`). */
  onError?: (message: string) => void;
}

/**
 * The band-agnostic registration primitive underneath {@link registerPush}: it owns the race-safe Capacitor flow
 * (checkPermissions → requestPermissions → **listeners BEFORE `register()`** → `register()`) and hands the raw
 * APNs token to {@link onToken}, leaving the POST body shape to the caller. Core wraps it with its person body
 * ({@link registerPush}); the Antenna fan app wraps it with its bandId-less fan body (invariant 5 — fans are a
 * separate realm, so a fan token NEVER carries a band). Keeping the Capacitor mechanics here is the seam
 * boundary: no app imports `@capacitor/*` directly (only this package depends on the plugins).
 */
export interface RegisterPushTokenOptions {
  /** Called on the `'registration'` event with the raw APNs token — the caller POSTs it in its own body shape. */
  onToken: (token: string) => void | Promise<void>;
  /** Optional registration-error sink (APNs `registrationError`). */
  onError?: (message: string) => void;
}

export type PushRegisterStatus = "registered" | "denied" | "skipped-web";

/** The registration listeners currently attached — removed and re-added on each {@link registerPush} so a
 * re-register never STACKS listeners (which would double-POST). Kept separate from the deep-link
 * tap listeners (installed once) so re-registering never tears those down. */
let registrationHandles: PluginListenerHandle[] = [];

/** The APNs token this device last registered, so logout can tell the server to stop pushing to it (ART-259 C8).
 * Without it a signed-out phone keeps receiving the previous person's notifications — the server knows the token
 * but not that this person left the device. Memory-only: every launch re-registers, so it is set again. */
let lastRegisteredToken: string | null = null;

/** The device's last-registered APNs token, or null (web, permission denied, or not registered yet this launch). */
export function lastPushToken(): string | null {
  return lastRegisteredToken;
}

/**
 * The push registration flow: guard (native + signed-in-with-pref-on is the caller's precondition)
 * → checkPermissions → requestPermissions → **wire listeners BEFORE `register()`** (the race fix: Capacitor's
 * `addListener` is async and `register()` can fire `'registration'` before the listener attaches, dropping the
 * token) → `register()`. On the `'registration'` event the raw APNs token is handed to {@link onToken}. A
 * re-register (a new sign-in, or the fan's /you toggle) rebinds cleanly: the PRIOR registration listeners are
 * removed by handle first, so re-registering never STACKS them (which would multi-deliver the token). A no-op on
 * web (there is no device token in a browser).
 */
export async function registerPushToken(options: RegisterPushTokenOptions): Promise<PushRegisterStatus> {
  if (!Capacitor.isNativePlatform()) return "skipped-web";

  let permission = await PushNotifications.checkPermissions();
  if (permission.receive === "prompt" || permission.receive === "prompt-with-rationale") {
    permission = await PushNotifications.requestPermissions();
  }
  if (permission.receive !== "granted") return "denied";

  // Remove the PRIOR registration listeners (a re-register) before re-adding — surgically, by handle, so the
  // deep-link tap listeners installed once by installNativeDeepLinkListeners survive.
  await Promise.all(registrationHandles.map((handle) => handle.remove()));
  registrationHandles = [];

  // Wire listeners BEFORE register() — the race fix.
  registrationHandles.push(
    await PushNotifications.addListener("registration", (token) => {
      lastRegisteredToken = token.value;
      return options.onToken(token.value);
    }),
    await PushNotifications.addListener("registrationError", (error) =>
      options.onError?.(String(error?.error ?? "registration failed")),
    ),
  );

  await PushNotifications.register();
  return "registered";
}

/**
 * The CORE registration: {@link registerPushToken} wired to the core `/me/push/register` body. The device registers
 * for the signed-in person (ART-259) and reports the APNs environment the build was SIGNED for. The Antenna fan app uses {@link registerPushToken} directly — its fan body has NO band (invariant 5).
 */
export async function registerPush(options: RegisterPushOptions): Promise<PushRegisterStatus> {
  return registerPushToken({
    onToken: async (token) =>
      options.postToken({
        token,
        platform: getPlatform(),
        appEnv: await resolveApnsEnvironment(options.appEnv),
        appVersion: options.appVersion,
      }),
    onError: options.onError,
  });
}

/** Map an untrusted push `notification.data` payload to a raw route to sanitize+route, or null to drop the tap.
 * The default reads the CORE `data.route` contract; the Antenna fan app injects `parseAntennaPush`
 * (its frozen payload is `{kind, band_id}`, NOT `data.route`, so the default would silently drop every fan tap). */
export type PushRouteTranslator = (data: Record<string, unknown> | undefined) => string | null;

export interface DeepLinkListenerOptions {
  /** Override how a push tap's payload maps to a route (defaults to the core `data.route` contract). */
  translatePush?: PushRouteTranslator;
}

/** The core payload contract: the server writes a pre-sanitized `data.route`.*/
const defaultTranslatePush: PushRouteTranslator = (data) => {
  const route = data?.["route"];
  return typeof route === "string" ? route : null;
};

/**
 * Install the native deep-link listeners: a universal-link open (`App.appUrlOpen`) and a push tap
 * (`pushNotificationActionPerformed`) both feed the {@link DeepLinkController}, which sanitizes then navigates
 * or parks (cold-launch). Installed ONCE at boot; a no-op on web (the browser uses `window.location` +
 * `?to=` instead). Returns a cleanup that removes the listeners.
 *
 * The push tap's payload is mapped to a route by {@link DeepLinkListenerOptions.translatePush}, defaulting to the
 * core `data.route` contract; an app whose payload differs (the Antenna fan app: `{kind, band_id}`) injects its
 * own translator. The universal-link path is app-agnostic (a same-origin path, allowlist-checked by the
 * controller), so it needs no override.
 */
export async function installNativeDeepLinkListeners(
  controller: DeepLinkController,
  options?: DeepLinkListenerOptions,
): Promise<() => void> {
  if (!Capacitor.isNativePlatform()) return () => {};

  const translatePush = options?.translatePush ?? defaultTranslatePush;
  const handles: PluginListenerHandle[] = [];

  // Universal link (applinks:artistcntrl.app): resolve the path; honour a legacy `?to=<route>` if present.
  handles.push(
    await App.addListener("appUrlOpen", (event) => {
      try {
        const url = new URL(event.url);
        const to = parseToParam(url.search);
        controller.handle(to ?? url.pathname + url.search + url.hash);
      } catch {
        // a malformed URL is dropped, not thrown
      }
    }),
  );

  // Push tap: translate the untrusted payload to a raw route, then route it through the controller's sanitizer
  // (the controller re-checks the same-origin + root allowlist — defense in depth even for an already-clean path).
  handles.push(
    await PushNotifications.addListener("pushNotificationActionPerformed", (action) => {
      const data = action.notification?.data as Record<string, unknown> | undefined;
      controller.handle(translatePush(data));
    }),
  );

  return () => {
    void Promise.all(handles.map((handle) => handle.remove()));
  };
}

/** Reset the module-level registration-listener handles (tests only). */
export function _resetPushRegistrationForTest(): void {
  registrationHandles = [];
  lastRegisteredToken = null;
}
